import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Building2, Mail, Phone } from "lucide-react";
import { dateShort } from "@/lib/format";

export default function Customers() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/addresses"), api.get("/quotes")])
      .then(([a, q]) => {
        setAddresses(a.data);
        setQuotes(q.data);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const customers = useMemo(() => {
    const map = new Map();
    for (const a of addresses) {
      const key = (a.company || "").trim().toLowerCase();
      if (!key) continue;
      if (!map.has(key)) map.set(key, { company: a.company.trim(), locations: [], contact_name: "", contact_email: "", contact_phone: "" });
      const c = map.get(key);
      c.locations.push(a);
      if (!c.contact_name && a.contact_name) c.contact_name = a.contact_name;
      if (!c.contact_email && a.contact_email) c.contact_email = a.contact_email;
      if (!c.contact_phone && a.contact_phone) c.contact_phone = a.contact_phone;
    }
    return Array.from(map.values()).sort((a, b) => a.company.localeCompare(b.company));
  }, [addresses]);

  const quoteIndex = useMemo(() => {
    const idx = new Map();
    for (const q of quotes) {
      const key = (q.customer_name || "").trim().toLowerCase();
      if (!key) continue;
      if (!idx.has(key)) idx.set(key, { lastQuotedAt: null, lastRep: "", lastBookedAt: null });
      const e = idx.get(key);
      const t = q.created_at;
      if (t && (!e.lastQuotedAt || t > e.lastQuotedAt)) {
        e.lastQuotedAt = t;
        e.lastRep = q.created_by || "";
      }
      if (["won", "booked"].includes(q.status) && t && (!e.lastBookedAt || t > e.lastBookedAt)) {
        e.lastBookedAt = t;
      }
    }
    return idx;
  }, [quotes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        c.company.toLowerCase().includes(q) ||
        c.contact_name.toLowerCase().includes(q) ||
        c.contact_email.toLowerCase().includes(q) ||
        c.locations.some((l) => `${l.city} ${l.state} ${l.zip}`.toLowerCase().includes(q)),
    );
  }, [customers, search]);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="customers-page">
      <div className="sticky top-14 z-20 -mx-4 border-b border-border bg-background/95 px-4 pb-4 pt-1 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="mb-3">
          <h1 className="font-heading text-3xl font-black tracking-tighter">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {filtered.length} of {customers.length} customers · {addresses.length} saved locations
          </p>
        </div>
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search company, contact, city, ZIP…" className="h-10 pl-9" data-testid="customers-search" />
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Company</TableHead>
                <TableHead className="text-right">Locations</TableHead>
                <TableHead>Primary Location</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email / Phone</TableHead>
                <TableHead>Rep</TableHead>
                <TableHead>Last Quoted</TableHead>
                <TableHead>Last Booked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={8} className="py-12 text-center text-muted-foreground">Loading customers…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                  <Building2 className="mx-auto mb-2 h-7 w-7 opacity-40" />No customers found.
                </TableCell></TableRow>
              )}
              {filtered.map((c) => {
                const primary = c.locations[0] || {};
                const info = quoteIndex.get(c.company.trim().toLowerCase()) || {};
                return (
                  <TableRow
                    key={c.company}
                    className="cursor-pointer border-border hover:bg-secondary/40"
                    data-testid={`customer-row-${c.company}`}
                    onClick={() => navigate(`/customers/${encodeURIComponent(c.company)}`)}
                  >
                    <TableCell className="text-sm font-semibold">{c.company}</TableCell>
                    <TableCell className="metric-num text-right text-sm">{c.locations.length}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[primary.city, primary.state].filter(Boolean).join(", ") || "—"}{primary.zip ? ` ${primary.zip}` : ""}
                    </TableCell>
                    <TableCell className="text-sm">{c.contact_name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c.contact_email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.contact_email}</div>}
                      {c.contact_phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.contact_phone}</div>}
                      {!c.contact_email && !c.contact_phone && "—"}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`customer-rep-${c.company}`}>{info.lastRep || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{info.lastQuotedAt ? dateShort(info.lastQuotedAt) : "—"}</TableCell>
                    <TableCell className="text-xs">
                      {info.lastBookedAt ? <span className="font-medium text-[hsl(var(--success))]">{dateShort(info.lastBookedAt)}</span> : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
